import { motion } from "framer-motion";
import { useReward } from "partycles";
import type { ComponentType } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { HeartSvg } from "../Svg/Icons/Heart";
import type { SvgProps } from "../Svg/types";

type TimelineItem = {
  id: string | number;
  title: string;
  description: string;
  date?: string;
  elId?: string;
  icon: ComponentType<SvgProps>;
};

type TimeLineProps = {
  items: TimelineItem[];
  lineColor?: string;
  trackColor?: string;
};

export const TimeLine = ({
  items,
  lineColor = "var(--color-primary-text)",
  trackColor = "rgba(143, 142, 111, 0.18)",
}: TimeLineProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [progressPx, setProgressPx] = useState(0);
  const [activeItems, setActiveItems] = useState<boolean[]>([]);
  const md = useIsDesktop();

  const confettiSpent = useRef<boolean>(null);
  const { reward } = useReward("confetti", "confetti", {
    particleCount: 120,
    spread: 50,
    colors: [
      "#f44336",
      "#e91e63",
      "#9c27b0",
      "#673ab7",
      "#3f51b5",
      "#2196f3",
      "#03a9f4",
      "#00bcd4",
      "#009688",
      "#4CAF50",
      "#8BC34A",
      "#CDDC39",
      "#FFEB3B",
      "#FFC107",
      "#FF9800",
      "#FF5722",
    ],
    // startVelocity: 15,
    elementSize: 8,
    // startVelocity: 1,
    // NEW: Enable paper flutter effect
    physics: {
      friction: md ? undefined : 0.95,
      gravity: md ? 0.85 : 0.4,
    },
    effects: { flutter: true },
  });

  useEffect(() => {
    const updateTimeline = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const viewportMiddle = window.innerHeight / 2;

      const totalHeight = containerRect.height;

      const nextProgressPx = Math.max(
        0,
        Math.min(viewportMiddle - containerRect.top, totalHeight),
      );

      setProgressPx(nextProgressPx);

      const nextActiveItems = items.map((_, index) => {
        const el = itemRefs.current[index];
        if (!el) return false;

        const rect = el.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;

        return itemCenter <= viewportMiddle;
      });

      setActiveItems(nextActiveItems);
    };

    updateTimeline();

    window.addEventListener("scroll", updateTimeline, { passive: true });
    window.addEventListener("resize", updateTimeline);

    return () => {
      window.removeEventListener("scroll", updateTimeline);
      window.removeEventListener("resize", updateTimeline);
    };
  }, [items]);

  const progressPercent = useMemo(() => {
    const container = containerRef.current;
    if (!container) return 0;

    return Math.max(
      0,
      Math.min((progressPx / container.offsetHeight) * 100, 100),
    );
  }, [progressPx]);

  return (
    <section className="relative px-2 py-6 text-slate-100 md:px-6">
      <div className="mx-auto max-w-6xl">
        <div ref={containerRef} className="relative mx-auto max-w-5xl">
          <div
            className="absolute left-1/1 md:left-1/2 top-0 h-full w-1 -translate-x-1/2 rounded-full block"
            style={{ backgroundColor: trackColor }}
          />

          <motion.div
            className="absolute left-1/1 md:left-1/2 top-0 w-1 -translate-x-1/2 rounded-full block"
            style={{ backgroundColor: lineColor }}
            animate={{ height: `${progressPercent}%` }}
            transition={{ duration: 0.12, ease: "linear" }}
          />

          <div className="space-y-8 md:space-y-6">
            {items.map((item, index) => {
              const isLeft = md ? index % 2 === 0 : true;
              const isActive = activeItems[index];

              return (
                <div
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  className="relative grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20"
                >
                  <div
                    className={`block  ${
                      isLeft ? "pr-12 md:pr-0 " : "col-start-2 "
                    }${md && isLeft ? "text-right" : ""}`}
                  >
                    <motion.div
                      initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true, amount: 0.35 }}
                      transition={{ duration: 0.5 }}
                      className="rounded-3xl  text-primary-text "
                    >
                      {item.date && (
                        <p
                          className={`eyebrow-md opacity-80  mb-3 ${isLeft && md ? "pr-3" : "pl-3"}`}
                        >
                          {item.date}
                        </p>
                      )}
                      <div
                        className={`flex items-center rounded-4xl px-1 py-1 ${isLeft && md ? "justify-end" : ""}`}
                        style={{
                          background:
                            isLeft && md
                              ? "linear-gradient(to left, white, transparent 125%)"
                              : "linear-gradient(to right, white, transparent 125%)",
                        }}
                      >
                        {(!isLeft || !md) && (
                          <div className="mr-3 rounded-full">
                            <item.icon
                              width={md ? "70px" : "60px"}
                              color="var(--color-primary-text)"
                            />
                          </div>
                        )}
                        <h3 className="text-md md:text-lg font-medium">
                          {item.title}
                        </h3>

                        {isLeft && md && (
                          <div className="ml-3 rounded-full">
                            <item.icon
                              width={md ? "70px" : "60px"}
                              color="var(--color-primary-text)"
                            />
                          </div>
                        )}
                      </div>
                      <p className="mt-3 leading-7">{item.description}</p>
                    </motion.div>
                  </div>

                  <div className="pointer-events-none absolute left-1/1 md:left-1/2 top-9 -translate-x-1/2 translate-y-1/1  block">
                    <motion.div
                      animate={{
                        scale: isActive ? 2 : 1,
                        backgroundColor: isActive ? "" : trackColor,
                        borderColor: isActive ? "" : trackColor,
                      }}
                      className={`h-5 w-5 rounded-full flex justify-center items-center ${isActive ? "" : "border-2"}`}
                      transition={{ duration: 0.3 }}
                      onAnimationStart={(def) => {
                        if (
                          item.elId === "confetti" &&
                          (def as any).scale === 2 &&
                          !confettiSpent.current
                        ) {
                          confettiSpent.current = true;
                          reward();
                        }
                      }}
                    >
                      <div style={{ width: "15px" }}>
                        {isActive && <HeartSvg width="100%" color="#ff0505" />}
                      </div>
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
